/**
 * Doc-group coverage primitives — the TAXONOMY-first view of a loan's documents.
 *
 * The Documents tab's "DocGroup Coverage" sub-view renders the whole board (every
 * group, search, scope filters, collapse). Some cards want the same thing for ONE
 * group, inline: the Contacts tab's General Contractor card shows the "General
 * Contractor Docs" group beside the GC record, so the UW confirmations sit next to
 * the paperwork they attest to.
 *
 * This module owns the pieces below the board — the type row, the file row and the
 * per-group model — so the two surfaces can never drift. DocGroupCoverageView
 * imports them rather than keeping its own copies.
 *
 * Data, same as the full view:
 *  - catalog → `useFileDocGroupAndTypesHook().fileDocGroupAndTypes`
 *  - uploads → `state.loanDrilldown.loanDocuments`, hidden files excluded. A doc
 *    whose docType isn't in the catalog is appended rather than dropped, so a
 *    group's count always reconciles to what the loan actually holds.
 *
 * "Missing" here means "no file uploaded", not "required and absent" — the
 * Documents tab's Missing sub-view owns requirements.
 */
import ArrowUpwardRounded from "@mui/icons-material/ArrowUpwardRounded";
import HourglassEmptyOutlined from "@mui/icons-material/HourglassEmptyOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import LinkRounded from "@mui/icons-material/LinkRounded";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import { Box, ButtonBase, Tooltip } from "@mui/material";
import { useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";

import { formatDateTime } from "../../../_src_shared/utils/functions/formatDateTime";
import { isDocHidden } from "../../../_src_shared/utils/functions/isDocHidden";
import Txt from "../../../components/typography/Txt";
import { getExpirationDaysForDocType } from "../../../constants/documents/docTypeExpirationDurations";
import { documentStatusLabel } from "../../../constants/selectOptions/selectOptionsDocumentStatus";
import { useFileDocGroupAndTypesHook } from "../../../hooks/useFileDocGroupAndTypesHook";
import { docUploadModalOpen } from "../../../redux/actions/docUploadModalActions";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import { formatDate } from "../../../_src_shared/utils/functions/formatDate";
import { getValidityReferenceMs } from "../../../utils/documents/validityReference";
import { amber, slate, status, teal } from "../../../theme/colors";
import { getDocExpiration } from "../tabs/LoanDrilldownDocumentsTab/DocExpirationCell";
import { useUploadQueueOptional } from "../tabs/LoanDrilldownDocumentsTab/UploadQueueContext";

// Status chip palette — the same pairs the Uploaded tab's status pills use,
// keyed by the canonical documentStatus value.
export const STATUS_CHIP = {
  Accepted: { bg: status.success.bg, fg: status.success.fg },
  "Pending Review": { bg: status.warning.bg, fg: status.warning.fg },
  Incomplete: { bg: amber.light, fg: amber.chipText },
  Rejected: { bg: status.error.bg, fg: status.error.fg },
  "New Upload": { bg: status.neutral.bg, fg: status.neutral.fg },
  "Processor Reviewed": { bg: status.info.bg, fg: status.info.fg },
};
export const DEFAULT_CHIP = STATUS_CHIP["New Upload"];

// Row-level trouble dots: a type bubbles up its worst file. Rejected beats
// Incomplete.
export const WARN_COLOR = {
  rejected: status.error.solid,
  incomplete: amber.main,
};

// Validity badge palette, keyed by the urgency bucket `getDocExpiration`
// returns. Token-derived so it tracks the status colors everywhere else; an
// expired document earns the solid fill because it is not a warning any more.
export const VALIDITY_TONE = {
  ok: { bg: status.success.bg, fg: status.success.fg },
  warning: { bg: status.warning.bg, fg: status.warning.fg },
  critical: { bg: status.error.bg, fg: status.error.fg },
  expired: { bg: status.error.solid, fg: "#ffffff" },
  noStart: { bg: slate[100], fg: slate[600] },
};

export const EMPTY_DOT = slate[300];
export const LINE = slate[200];
export const LINE_SOFT = slate[100];
export const INK_2 = slate[600];
export const INK_3 = slate[400];

export const keyOf = (group, type) => `${group}|${type}`;

/**
 * The day document expirations are measured against: today while the loan is
 * open, the loan's own anchor date once it has closed — the same reference the
 * Documents tab's Expires column uses, so a coverage row and the table can't
 * disagree about whether a report is still good.
 */
export const useValidityNowMs = () => {
  const loanStatus = useSelector((s) => s.loanDrilldown?.loanStatus);
  const loanClosed = useSelector((s) => s.loanDrilldown?.loanClosed);
  const originationDate = useSelector((s) => s.loanDrilldown?.originationDate);
  const fundedDate = useSelector((s) => s.loanDrilldown?.fundedDate);
  const estimatedClosingDate = useSelector(
    (s) => s.loanDrilldown?.estimatedClosingDate,
  );
  const expectedClosingDate = useSelector(
    (s) => s.loanDrilldown?.expectedClosingDate,
  );
  const closingScheduledDate = useSelector(
    (s) => s.loanDrilldown?.closingScheduledDate,
  );
  return useMemo(
    () =>
      getValidityReferenceMs({
        loanStatus,
        loanClosed,
        originationDate,
        fundedDate,
        estimatedClosingDate,
        expectedClosingDate,
        closingScheduledDate,
      }),
    [
      loanStatus,
      loanClosed,
      originationDate,
      fundedDate,
      estimatedClosingDate,
      expectedClosingDate,
      closingScheduledDate,
    ],
  );
};

// Plain-language validity state, the tooltip's lead line.
const validityStatusLine = (exp) => {
  if (exp?.noStart) return "No effective date set";
  const rem = exp?.remaining ?? 0;
  if (rem < 0)
    return `Expired ${Math.abs(rem)} day${Math.abs(rem) === 1 ? "" : "s"} ago`;
  if (rem === 0) return "Expires today";
  return `Valid — expires in ${rem} day${rem === 1 ? "" : "s"}`;
};

// Date-only report fields — formatDate, never formatDateTime (which would shift
// them a day earlier in a negative-offset zone).
const reportDate = (iso) => formatDate(iso, "—", "M/d/yyyy");

/**
 * Document validity badge — the same shelf-life marker the Documents table puts
 * in its filename cell: an hourglass while the document is good, a warning glyph
 * once it is nearly or already past due, colored by urgency, with the effective
 * date / duration / expiry on hover. Renders nothing for a document type that
 * carries no validity window.
 */
export const DocValidityBadge = ({ doc }) => {
  const nowMs = useValidityNowMs();
  const exp = getDocExpiration(doc, nowMs);
  if (!exp) return null;
  const tone = VALIDITY_TONE[exp.noStart ? "noStart" : exp.kind] ||
    VALIDITY_TONE.ok;
  const BadgeIcon =
    exp.kind === "expired" || exp.kind === "critical"
      ? WarningAmberOutlined
      : HourglassEmptyOutlined;
  const detail = [
    doc?.reportEffectiveDate
      ? `Effective: ${reportDate(doc.reportEffectiveDate)}`
      : "Effective: not set",
    exp.days != null
      ? `Duration: ${exp.days} day${exp.days === 1 ? "" : "s"}`
      : null,
    exp.expiration ? `Expires: ${reportDate(exp.expiration)}` : null,
  ].filter(Boolean);
  return (
    <Tooltip
      placement="top"
      title={
        <Box sx={{ py: 0.25 }}>
          <Box sx={{ ...TOOLTIP_EYEBROW }}>Document validity</Box>
          <Box sx={{ fontSize: 12, fontWeight: 600 }}>
            {validityStatusLine(exp)}
          </Box>
          {detail.map((line) => (
            <Box key={line} sx={{ fontSize: 11, opacity: 0.85 }}>
              {line}
            </Box>
          ))}
        </Box>
      }
    >
      <Box
        sx={{
          flex: "none",
          p: "3px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: "6px",
          background: tone.bg,
          color: tone.fg,
        }}
      >
        <BadgeIcon sx={{ fontSize: "12px !important" }} />
      </Box>
    </Tooltip>
  );
};

const TOOLTIP_EYEBROW = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  opacity: 0.7,
  mb: "2px",
};

// ---------------------------------------------------------------------------
// Search-term highlight
// ---------------------------------------------------------------------------
export const Highlight = ({ text, term }) => {
  const value = String(text ?? "");
  if (!term) return value;
  const idx = value.toLowerCase().indexOf(term);
  if (idx < 0) return value;
  return (
    <>
      {value.slice(0, idx)}
      <Box
        component="mark"
        sx={{
          background: teal[100],
          color: "inherit",
          borderRadius: "3px",
          px: "1px",
        }}
      >
        {value.slice(idx, idx + term.length)}
      </Box>
      {value.slice(idx + term.length)}
    </>
  );
};

// ---------------------------------------------------------------------------
// One uploaded file under a type
// ---------------------------------------------------------------------------
export const FileRow = ({ doc, onPreview }) => {
  const name = doc?.file_display_name || doc?.name || "Untitled";
  const chip = STATUS_CHIP[doc?.documentStatus] || DEFAULT_CHIP;
  const statusText = documentStatusLabel(doc?.documentStatus) || "New Upload";
  const date = doc?.uploadDate ? formatDateTime(doc.uploadDate, "M/d/yy") : "";
  // Everything the old right-hand status pill said, plus the context it never
  // had room for — the dot carries it on hover instead of spending a third of
  // a narrow row on a text chip.
  const facts = [
    `Status: ${statusText}`,
    doc?.docType ? `Type: ${doc.docType}` : null,
    doc?.docGroup ? `Group: ${doc.docGroup}` : null,
    doc?.uploadDate
      ? `Uploaded: ${formatDateTime(doc.uploadDate, "datetime")}`
      : null,
    doc?.uploadedBy ? `By: ${doc.uploadedBy}` : null,
  ].filter(Boolean);
  return (
    <ButtonBase
      onClick={(e) => {
        e.stopPropagation();
        onPreview?.(doc);
      }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        width: "100%",
        textAlign: "left",
        px: 0.5,
        py: 0.625,
        borderRadius: "6px",
        fontFamily: "inherit",
        "&:hover": { background: LINE_SOFT },
      }}
    >
      {/* Status dot + filename are ONE hover target — a 6px dot alone is not a
          hittable one. The dot is deliberately smaller than the 7px type-row dot
          so the two read as different levels rather than competing. */}
      <Tooltip
        placement="top"
        title={
          <Box sx={{ py: 0.25 }}>
            <Box sx={{ ...TOOLTIP_EYEBROW }}>Document</Box>
            <Box sx={{ fontSize: 12, fontWeight: 600 }}>{name}</Box>
            {facts.map((line) => (
              <Box key={line} sx={{ fontSize: 11, opacity: 0.85 }}>
                {line}
              </Box>
            ))}
          </Box>
        }
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              flex: "none",
              background: chip.fg,
            }}
          />
          <Txt
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              fontSize: 11,
              color: slate[900],
            }}
          >
            {name}
          </Txt>
        </Box>
      </Tooltip>
      <DocValidityBadge doc={doc} />
      <Txt sx={{ flex: "none", fontSize: 11, color: INK_3 }}>{date}</Txt>
    </ButtonBase>
  );
};

// ---------------------------------------------------------------------------
// One doc-type row (+ its inline file list when open)
// ---------------------------------------------------------------------------
// Hover pill on an empty type row — Upload New, with (when the surface can
// link files to a record) Assign Existing before it.
const HOVER_PILL_SX = {
  display: "none",
  flex: "none",
  alignItems: "center",
  gap: 0.5,
  border: `1px dashed ${teal[200]}`,
  background: "#fff",
  color: teal[700],
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 600,
  borderRadius: "6px",
  px: 1.125,
  py: "2px",
  "&:hover": { background: teal[100], borderStyle: "solid" },
};

// The actions in the header of a type that has files — "Upload" (and
// "Assign" on record-scoped surfaces). Solid hairline, so it reads as a real
// button next to the count rather than the empty-type dashed hint.
const HEADER_PILL_SX = {
  display: "none",
  flex: "none",
  alignItems: "center",
  gap: 0.5,
  border: `1px solid ${teal[200]}`,
  background: "#fff",
  color: teal[700],
  fontFamily: "inherit",
  fontSize: 11,
  fontWeight: 600,
  borderRadius: "6px",
  px: 1,
  py: "3px",
  "&:hover": { background: teal[100], borderColor: teal[300] },
};

/**
 * @param {(type) => void} [onAssign] — when given, an "Assign Existing" action
 *   sits before every Upload New (surfaces scoped to one record).
 */
export const TypeRow = ({
  type,
  term,
  open,
  onToggle,
  onUpload,
  onAssign,
  onPreview,
}) => {
  const has = type.files.length > 0;
  return (
    <Box>
      <Box
        role={has ? "button" : undefined}
        tabIndex={has ? 0 : undefined}
        aria-expanded={has ? open : undefined}
        onClick={has ? onToggle : undefined}
        onKeyDown={
          has
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onToggle();
                }
              }
            : undefined
        }
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.125,
          px: 1,
          py: 0.75,
          borderRadius: "7px",
          cursor: has ? "pointer" : "default",
          outline: "none",
          "&:hover": { background: has ? teal[50] : LINE_SOFT },
          "&:focus-visible": { boxShadow: `0 0 0 2px ${teal[300]}` },
          // Upload pill on an empty type reveals on hover / keyboard focus.
          "&:hover .cov-upload, &:focus-within .cov-upload": {
            display: "inline-flex",
          },
        }}
      >
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            flex: "none",
            background: has ? teal[500] : EMPTY_DOT,
          }}
        />
        <Tooltip title={type.name} enterDelay={800}>
          <Txt
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: 11,
              color: has ? slate[900] : INK_3,
              fontWeight: has ? 500 : 400,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Highlight text={type.name} term={term} />
          </Txt>
        </Tooltip>
        {!has && onAssign && (
          <ButtonBase
            className="cov-upload"
            onClick={(e) => {
              e.stopPropagation();
              onAssign(type);
            }}
            sx={HOVER_PILL_SX}
          >
            <LinkRounded sx={{ fontSize: 11 }} />
            Assign Existing
          </ButtonBase>
        )}
        {!has && (
          <ButtonBase
            className="cov-upload"
            onClick={(e) => {
              e.stopPropagation();
              onUpload(type);
            }}
            sx={HOVER_PILL_SX}
          >
            <ArrowUpwardRounded sx={{ fontSize: 11 }} />
            Upload New
          </ButtonBase>
        )}
        {type.warn && (
          <Tooltip title={`Contains a ${type.warn} file`}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                flex: "none",
                background: WARN_COLOR[type.warn],
              }}
            />
          </Tooltip>
        )}
        {has && (
          <Box
            component="span"
            sx={{
              flex: "none",
              fontSize: 11,
              fontWeight: 700,
              color: teal[700],
              background: teal[100],
              borderRadius: "999px",
              px: 1,
              py: "1px",
            }}
          >
            {type.files.length}
          </Box>
        )}
        {/* A type WITH files keeps its actions in this header (not a dashed
            box under the list): shown while the type is open, revealed on
            hover / keyboard focus while it is closed. */}
        {has && onAssign && (
          <ButtonBase
            className="cov-upload"
            onClick={(e) => {
              e.stopPropagation();
              onAssign(type);
            }}
            sx={{ ...HEADER_PILL_SX, ...(open && { display: "inline-flex" }) }}
          >
            <LinkRounded sx={{ fontSize: 12 }} />
            Assign
          </ButtonBase>
        )}
        {has && (
          <ButtonBase
            className="cov-upload"
            onClick={(e) => {
              e.stopPropagation();
              onUpload(type);
            }}
            sx={{ ...HEADER_PILL_SX, ...(open && { display: "inline-flex" }) }}
          >
            <ArrowUpwardRounded sx={{ fontSize: 12 }} />
            Upload
          </ButtonBase>
        )}
        {has && (
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 14,
              color: INK_3,
              flex: "none",
              transition: "transform .15s",
              transform: open ? "rotate(180deg)" : "none",
            }}
          />
        )}
      </Box>
      {has && open && (
        <Box
          sx={{
            mt: "2px",
            mb: 0.75,
            mr: 1,
            ml: 3,
            borderLeft: `2px solid ${LINE_SOFT}`,
            pl: 1.25,
          }}
        >
          {type.files.map((doc) => (
            <FileRow key={doc._id} doc={doc} onPreview={onPreview} />
          ))}
        </Box>
      )}
    </Box>
  );
};

/**
 * The coverage model for ONE doc group: every catalog type with the loan's
 * matching files, plus any uploaded type the catalog doesn't know about.
 *
 * @param {string} groupName exact docGroup, e.g. "General Contractor Docs"
 * @param {object[]} [docs] scope the uploads to these documents (e.g. one
 *   borrower's associated files) instead of every document on the loan
 * @returns {{ types: {name, files, warn}[], withDocs: number, total: number,
 *             fileCount: number }}
 */
export const useDocGroupCoverage = (groupName, docs) => {
  const allLoanDocuments = useSelector((s) => s.loanDrilldown?.loanDocuments);
  const loanDocuments = docs || allLoanDocuments;
  const { fileDocGroupAndTypes } = useFileDocGroupAndTypesHook();

  return useMemo(() => {
    const catalogTypes = [...((fileDocGroupAndTypes || {})[groupName] || [])];
    const uploads = {};
    (loanDocuments || []).forEach((doc) => {
      if (isDocHidden(doc)) return;
      if ((doc?.docGroup || "") !== groupName) return;
      const t = doc?.docType || "Uncategorized";
      if (!catalogTypes.includes(t)) catalogTypes.push(t);
      if (!uploads[t]) uploads[t] = [];
      uploads[t].push(doc);
    });

    const types = catalogTypes.map((name) => {
      const files = uploads[name] || [];
      const warn = files.some((f) => f.documentStatus === "Rejected")
        ? "rejected"
        : files.some((f) => f.documentStatus === "Incomplete")
          ? "incomplete"
          : null;
      return { name, files, warn };
    });

    return {
      types,
      withDocs: types.filter((t) => t.files.length).length,
      total: types.length,
      fileCount: types.reduce((n, t) => n + t.files.length, 0),
    };
  }, [fileDocGroupAndTypes, loanDocuments, groupName]);
};

/**
 * Upload plumbing for a coverage surface: one hidden file input retargeted per
 * type. Chosen files are staged into the bottom-docked upload queue with
 * docGroup / docType (and the auto validity window) already set — the same
 * path the Documents tab uses, so the operator skips the queue-then-classify
 * step.
 *
 * Inside an UploadQueueProvider (the Documents tab) they land in that queue.
 * Outside one (the Documents quick-view sidebar, Contacts, per-record cards)
 * the global upload modal opens with those files ALREADY in its queue — the
 * same review bar, pre-classified — rather than an empty modal.
 *
 * @param {{ type: "borrower"|"property", id: string }} [assignTo] pre-assign
 *   the staged files to this record (the queue's association picker; the
 *   modal is locked to it)
 * @returns {{ uploadTo: (docGroup, docType) => void, inputRef, onInputChange }}
 */
export const useCoverageUpload = (assignTo) => {
  const dispatch = useDispatch();
  const queue = useUploadQueueOptional();
  const inputRef = useRef(null);
  const pendingRef = useRef(null);

  const uploadTo = useCallback((docGroup, docType) => {
    pendingRef.current = { docGroup, docType };
    inputRef.current?.click();
  }, []);

  const onInputChange = useCallback(
    (e) => {
      const target = pendingRef.current;
      const fileList = e.target.files;
      if (target && fileList?.length) {
        const days = getExpirationDaysForDocType(target.docType);
        const entries = Array.from(fileList).map((file) => ({
          id: uuidv4(),
          file,
          name: file.name,
          file_display_name: file.name,
          docGroup: target.docGroup,
          docType: target.docType,
          reportGuidelineExpirationDays: days ?? null,
        }));
        if (queue?.onFilesAdded) {
          if (assignTo?.type && assignTo?.id) {
            queue.setAssociationTarget?.({
              type: assignTo.type,
              id: assignTo.id,
            });
          }
          queue.onFilesAdded(entries);
        } else {
          dispatch(
            docUploadModalOpen(assignTo || null, { initialFiles: entries }),
          );
        }
      }
      pendingRef.current = null;
      e.target.value = ""; // allow re-selecting the same file
    },
    [dispatch, queue, assignTo],
  );

  return { uploadTo, inputRef, onInputChange };
};

/** Preview dispatcher — a coverage file row is always clickable. */
export const useCoveragePreview = () => {
  const dispatch = useDispatch();
  return useCallback(
    (doc) => {
      if (doc) dispatch(filePreviewSetData(doc));
    },
    [dispatch],
  );
};
